import { SCR_UNIT_OPTIONS } from '../utils/clinicalUnits';

/**
 * 协变量注册表 — 支持从 API 动态加载，硬编码作为 fallback
 *
 * 优先从 GET /api/covariates/definitions 加载。
 * API 不可用时降级到硬编码 FALLBACK_COVARIATE_REGISTRY。
 */

export interface CovariateDefinition {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select';
  default_value?: any;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  tooltip?: string;
  options?: { label: string; value: any; agent_aliases?: string[] }[];
  group: 'demographics' | 'labs' | 'conditions' | 'genetics';
  label_en?: string;
  extraction_desc?: string;
  agent_aliases?: string[];
  derived_from?: string[];
  derived_formula?: string;
  companions?: string[];
  unit_variants?: UnitVariant[];
  r_script_aliases?: { alias_name?: string; normalized_alias?: string }[];
  sort_order?: number;
}

/** 单位变体定义（来自 covariate_unit_variants 表） */
export interface UnitVariant {
  id?: string;
  unit: string;
  is_standard: boolean;
  extraction_desc?: string;
  min_val?: number;
  max_val?: number;
  step_val?: number;
  placeholder?: string;
  conversion_formula?: string;
  sort_order?: number;
}

/** @deprecated CustomCovariateDefinition — use CovariateDefinition directly */
export type CustomCovariateDefinition = CovariateDefinition & {
  r_variable?: string;
  r_value_map?: Record<string, any>;
  r_variable_map?: Record<string, Record<string, number>>;
};

// ─── Fallback 硬编码注册表 ──────────────────────────────────────────────────

export const FALLBACK_COVARIATE_REGISTRY: Record<string, CovariateDefinition> = {
  weight: { key: 'weight', label: '体重', unit: 'kg', type: 'number', min: 0.3, max: 200, step: 0.1, group: 'demographics', placeholder: '如: 2.9' },
  height: { key: 'height', label: '身高', unit: 'cm', type: 'number', min: 20, max: 200, step: 0.5, group: 'demographics', placeholder: '如: 48' },
  gestational_age: { key: 'gestational_age', label: '胎龄', unit: '周', type: 'number', min: 22, max: 44, step: 0.5, group: 'demographics', tooltip: '出生时的孕周', placeholder: '如: 32' },
  age_pna: { key: 'age_pna', label: '出生后日龄', unit: '天', type: 'number', min: 0, max: 365, step: 1, group: 'demographics', placeholder: '如: 7' },
  age_pma: { key: 'age_pma', label: '纠正胎龄 PMA', unit: '周', type: 'number', min: 22, max: 300, step: 0.1, group: 'demographics', tooltip: '胎龄 + 出生后日龄/7', placeholder: '自动计算', derived_from: ['gestational_age', 'age_pna'], derived_formula: 'configured in backend derivation rules' },
  age_years: { key: 'age_years', label: '年龄', unit: '岁', type: 'number', min: 0, max: 120, step: 0.001, group: 'demographics', placeholder: '如: 5', derived_from: ['age_pna'], derived_formula: 'configured in backend derivation rules' },
  gender: { key: 'gender', label: '性别', type: 'select', options: [{ label: '男', value: 'male' }, { label: '女', value: 'female' }], group: 'demographics' },
  scr: { key: 'scr', label: '血清肌酐', type: 'number', min: 0, max: 1500, step: 0.1, group: 'labs', placeholder: '如: 0.8' },
  scr_unit: { key: 'scr_unit', label: '肌酐单位', type: 'select', options: [...SCR_UNIT_OPTIONS], group: 'labs' },
  albumin: { key: 'albumin', label: '白蛋白', unit: 'g/L', type: 'number', min: 0, max: 60, step: 0.1, group: 'labs', placeholder: '如: 35' },
  alt: { key: 'alt', label: 'ALT', unit: 'U/L', type: 'number', min: 0, max: 5000, step: 1, group: 'labs' },
  ast: { key: 'ast', label: 'AST', unit: 'U/L', type: 'number', min: 0, max: 5000, step: 1, group: 'labs' },
  crcl: { key: 'crcl', label: '肌酐清除率', unit: 'mL/min', type: 'number', min: 0, max: 300, step: 1, group: 'labs', derived_from: ['scr', 'scr_unit'], derived_formula: 'configured in backend derivation rules' },
  egfr: { key: 'egfr', label: 'eGFR', unit: 'mL/min/1.73m²', type: 'number', min: 0, max: 300, step: 1, group: 'labs', tooltip: '估算肾小球滤过率', derived_from: ['scr', 'scr_unit'], derived_formula: 'configured in backend derivation rules' },
  cr_assay_method: { key: 'cr_assay_method', label: '肌酐检测方法', type: 'select', options: [{ label: '酶法', value: 'enzymatic' }, { label: 'Jaffe法', value: 'jaffe' }], group: 'labs' },
  ventilation: { key: 'ventilation', label: '机械通气', type: 'boolean', group: 'conditions' },
  inotropes: { key: 'inotropes', label: '正性肌力药', type: 'boolean', group: 'conditions' },
  ecmo: { key: 'ecmo', label: 'ECMO', type: 'boolean', group: 'conditions' },
  ibuprofen: { key: 'ibuprofen', label: '布洛芬', type: 'boolean', group: 'conditions' },
  bsa: { key: 'bsa', label: '体表面积', label_en: 'BSA', unit: 'm²', type: 'number', min: 0.05, max: 3, step: 0.01, group: 'demographics', placeholder: '如: 0.25', tooltip: '体表面积', derived_from: ['weight', 'height'], derived_formula: 'configured in backend derivation rules' },
  race: { key: 'race', label: '种族', label_en: 'Race', type: 'select', options: [{ label: '亚裔', value: 'asian' }, { label: '白人', value: 'caucasian' }, { label: '黑人', value: 'black' }, { label: '其他', value: 'other' }], group: 'demographics' },
  fluid_balance: { key: 'fluid_balance', label: '累积液体平衡', unit: 'mL/kg', type: 'number', min: -200, max: 500, step: 1, group: 'conditions', tooltip: '累积液体平衡量' },
  cyp2c19: { key: 'cyp2c19', label: 'CYP2C19基因型', type: 'select', options: [{ label: '未知', value: 'unknown' }, { label: 'EM', value: 'EM' }, { label: 'IM', value: 'IM' }, { label: 'PM', value: 'PM' }], group: 'genetics' },
};

// ─── 动态注册表（运行时从 API 加载后与 fallback 合并）─────────────────────

// Pre-built merged registries (fallback + dynamic) for fast lookup
let _mergedRegistry: Record<string, CovariateDefinition> | null = null;
let _mergedCompanions: Record<string, string[]> | null = null;
// Revision counter — bumped each time the dynamic registry is set
let _registryVersion = 0;
const _registryListeners = new Set<(version: number) => void>();

/** 从 API 响应转换为 CovariateDefinition */
function apiDefToLocal(d: any): CovariateDefinition {
  return {
    key: d.key,
    label: d.label,
    label_en: d.label_en,
    type: d.data_type || 'number',
    default_value: d.default_value,
    unit: d.unit,
    min: d.min_val,
    max: d.max_val,
    step: d.step_val,
    placeholder: d.placeholder,
    tooltip: d.tooltip,
    options: d.options,
    group: d.group_name || 'demographics',
    extraction_desc: d.extraction_desc,
    agent_aliases: Array.isArray(d.agent_aliases) ? d.agent_aliases : [],
    derived_from: d.derived_from,
    derived_formula: d.derived_formula,
    companions: d.companions,
    r_script_aliases: d.r_script_aliases,
    sort_order: d.sort_order,
  };
}

/** 设置从 API 加载的定义（由 api.ts 调用）
 *  动态定义与 fallback 合并 — API 优先，fallback 兜底缺失的 key（如 cyp2c19）
 */
export function setDynamicRegistry(apiDefs: any[]) {
  // Keep compatibility definitions available when the API omits legacy
  // fields, while allowing database metadata to override same-key entries.
  const reg: Record<string, CovariateDefinition> = { ...FALLBACK_COVARIATE_REGISTRY };
  const comp: Record<string, string[]> = { ...FALLBACK_COMPANIONS };
  for (const d of apiDefs) {
    if (!d?.key) continue;
    reg[d.key] = apiDefToLocal(d);
    if (d.companions && Array.isArray(d.companions)) {
      comp[d.key] = d.companions;
    }
  }
  _mergedRegistry = reg;
  _mergedCompanions = comp;
  _registryVersion++;
  _registryListeners.forEach((listener) => listener(_registryVersion));
}

/** 获取当前有效的注册表（API + fallback 合并） */
export function getRegistry(): Record<string, CovariateDefinition> {
  return _mergedRegistry || FALLBACK_COVARIATE_REGISTRY;
}

/** 注册表版本号 — 用于 React 依赖追踪 */
export function getRegistryVersion(): number {
  return _registryVersion;
}

export function subscribeRegistryChanges(listener: (version: number) => void): () => void {
  _registryListeners.add(listener);
  return () => {
    _registryListeners.delete(listener);
  };
}

// 向后兼容：COVARIATE_REGISTRY 作为 getter 代理
export const COVARIATE_REGISTRY = new Proxy(FALLBACK_COVARIATE_REGISTRY, {
  get(target, prop, receiver) {
    const reg = _mergedRegistry || target;
    return Reflect.get(reg, prop, receiver);
  },
  ownKeys() {
    return Reflect.ownKeys(_mergedRegistry || FALLBACK_COVARIATE_REGISTRY);
  },
  getOwnPropertyDescriptor(target, prop) {
    const reg = _mergedRegistry || target;
    return Object.getOwnPropertyDescriptor(reg, prop);
  },
});

/** scr 选中时自动附带 scr_unit (fallback) */
const FALLBACK_COMPANIONS: Record<string, string[]> = { scr: ['scr_unit'] };

export const COVARIATE_COMPANIONS = new Proxy(FALLBACK_COMPANIONS, {
  get(target, prop, receiver) {
    const comp = _mergedCompanions || target;
    return Reflect.get(comp, prop, receiver);
  },
  ownKeys() {
    return Reflect.ownKeys(_mergedCompanions || FALLBACK_COMPANIONS);
  },
  getOwnPropertyDescriptor(target, prop) {
    const comp = _mergedCompanions || target;
    return Object.getOwnPropertyDescriptor(comp, prop);
  },
});

/** 获取协变量列表（含伴随字段），保持注册表中的顺序 */
export function expandCovariateKeys(keys: string[]): string[] {
  const companions = _mergedCompanions || FALLBACK_COMPANIONS;
  const result: string[] = [];
  const seen = new Set<string>();
  for (const k of keys) {
    if (seen.has(k)) continue;
    seen.add(k);
    result.push(k);
    const comps = companions[k] || [];
    for (const c of comps) {
      if (!seen.has(c)) {
        seen.add(c);
        result.push(c);
      }
    }
  }
  return result;
}

/**
 * 返回有效注册表。
 * Phase 3: 所有协变量（含 CYP2C19）已在 covariate_definitions 中，无需合并 custom_covariates。
 */
export function buildEffectiveRegistry(): Record<string, CovariateDefinition> {
  return getRegistry();
}
